export type MediaType = 'image' | 'video';

export interface FileValidationResult {
  valid: boolean;
  error?: string;
  mediaType?: MediaType;
}

// Must match backend validation in s3.client.service.ts
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm'];

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB

export function validateMediaFile(file: File): FileValidationResult {
  // Check if it's an image
  if (ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    if (file.size > MAX_IMAGE_SIZE) {
      return {
        valid: false,
        error: `Image size must be less than 10MB. Your file is ${formatFileSize(file.size)}.`,
      };
    }
    return {
      valid: true,
      mediaType: 'image',
    };
  }

  // Check if it's a video
  if (ACCEPTED_VIDEO_TYPES.includes(file.type)) {
    if (file.size > MAX_VIDEO_SIZE) {
      return {
        valid: false,
        error: `Video size must be less than 100MB. Your file is ${formatFileSize(file.size)}.`,
      };
    }
    return {
      valid: true,
      mediaType: 'video',
    };
  }

  // Unsupported file type
  return {
    valid: false,
    error: `Unsupported file type: ${file.type}. Please upload an image (PNG, JPEG, WebP) or video (MP4, WebM).`,
  };
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
}

export function isImageFile(file: File): boolean {
  return ACCEPTED_IMAGE_TYPES.includes(file.type);
}

export function isVideoFile(file: File): boolean {
  return ACCEPTED_VIDEO_TYPES.includes(file.type);
}

export function getAcceptedImageTypes(): string {
  return ACCEPTED_IMAGE_TYPES.join(',');
}

export function getAcceptedVideoTypes(): string {
  return ACCEPTED_VIDEO_TYPES.join(',');
}
